import { useState } from 'react';
import PropTypes from 'prop-types';
// @mui
import { styled } from '@mui/material/styles';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  Grid,
  Stack,
  Paper,
  alpha,
  Button,
} from '@mui/material';
// components
import { MotionViewport, varFade } from 'src/components/animate';
import Iconify from 'src/components/iconify';
import { m } from 'framer-motion';
// utils
import { restaurantSteps, supplierSteps, userTypeContent } from 'src/utils/how-it-works';

// ----------------------------------------------------------------------

const StyledRoot = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(10),
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(15),
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  textAlign: 'center',
  padding: theme.spacing(3),
  transition: theme.transitions.create(['transform', 'box-shadow']),
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.customShadows.z24,
  },
}));

const StyledStepNumber = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`,
  color: 'white',
  fontSize: '1.5rem',
  fontWeight: 'bold',
}));

// ----------------------------------------------------------------------

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`how-it-works-tabpanel-${index}`}
      aria-labelledby={`how-it-works-tab-${index}`}
      style={{ display: value === index ? 'block' : 'none' }}
      {...other}
    >
      <Box>{children}</Box>
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

export default function HowItWorks() {
  const [tabValue, setTabValue] = useState(0);
  const [userType, setUserType] = useState('restaurant'); // 'restaurant' or 'supplier'

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleUserTypeChange = (type) => {
    setUserType(type);
    setCurrentScreenshot(0); // Reset to first screenshot when switching user type
  };

  const [currentScreenshot, setCurrentScreenshot] = useState(0);

  const handleNextScreenshot = () => {
    const maxIndex = userTypeContent[userType].screenshots.length - 1;
    setCurrentScreenshot(currentScreenshot === maxIndex ? 0 : currentScreenshot + 1);
  };

  const handlePrevScreenshot = () => {
    const maxIndex = userTypeContent[userType].screenshots.length - 1;
    setCurrentScreenshot(currentScreenshot === 0 ? maxIndex : currentScreenshot - 1);
  };

  return (
    <StyledRoot>
      <Container maxWidth="lg" component={MotionViewport}>
        <m.div variants={varFade().inUp}>
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 1,
            }}
          >
            How It Works
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              mb: 6,
            }}
          >
            Discover how Cartify streamlines your supply chain operations
          </Typography>
        </m.div>

        <m.div variants={varFade().inUp}>
          <Paper
            sx={{
              borderRadius: 2,
              mb: 6,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="How it works tabs"
              centered
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  minHeight: 64,
                  fontSize: '1rem',
                  fontWeight: 600,
                },
              }}
            >
              <Tab
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon="mdi:food-fork-drink" />
                    <span>For Restaurants</span>
                  </Stack>
                }
                id="how-it-works-tab-0"
                aria-controls="how-it-works-tabpanel-0"
              />
              <Tab
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Iconify icon="mdi:truck-delivery" />
                    <span>For Suppliers</span>
                  </Stack>
                }
                id="how-it-works-tab-1"
                aria-controls="how-it-works-tabpanel-1"
              />
            </Tabs>
          </Paper>
        </m.div>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ pt: 3 }}>
            <Grid container spacing={4}>
              {restaurantSteps.map((step) => (
                <Grid item xs={12} sm={6} md={3} key={step.step}>
                  <StyledCard>
                    <StyledStepNumber>{step.step}</StyledStepNumber>
                    <Iconify
                      icon={step.icon}
                      sx={{
                        width: 48,
                        height: 48,
                        color: 'primary.main',
                        mb: 2,
                        display: 'block',
                        mx: 'auto',
                      }}
                    />
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ pt: 3 }}>
            <Grid container spacing={4}>
              {supplierSteps.map((step) => (
                <Grid item xs={12} sm={6} md={3} key={step.step}>
                  <StyledCard>
                    <StyledStepNumber>{step.step}</StyledStepNumber>
                    <Iconify
                      icon={step.icon}
                      sx={{
                        width: 48,
                        height: 48,
                        color: 'primary.main',
                        mb: 2,
                        display: 'block',
                        mx: 'auto',
                      }}
                    />
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {step.description}
                    </Typography>
                  </StyledCard>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        <m.div variants={varFade().inUp}>
          <Box
            sx={{
              mt: 8,
              p: 6,
              borderRadius: 3,
              background: (theme) =>
                `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
                  theme.palette.warning.main,
                  0.05
                )} 100%)`,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            {/* Background decorative elements */}
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                left: -50,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background: (theme) => alpha(theme.palette.primary.main, 0.1),
                zIndex: 0,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -30,
                right: -30,
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: (theme) => alpha(theme.palette.warning.main, 0.1),
                zIndex: 0,
              }}
            />

            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                variant="h3"
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  fontWeight: 'bold',
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  textAlign: 'center',
                  px: { xs: 1, sm: 0 },
                }}
              >
                Ready to Get Started?
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  mb: { xs: 3, sm: 4, md: 6 },
                  maxWidth: 600,
                  mx: 'auto',
                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                  textAlign: 'center',
                  px: { xs: 1, sm: 0 },
                }}
              >
                Join thousands of restaurants and suppliers who have already transformed their
                operations with Cartify.
              </Typography>

              {/* User Type Selection */}
              <Box sx={{ mb: { xs: 4, sm: 5, md: 6 } }}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    fontWeight: 'bold',
                    fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                    textAlign: 'center',
                    px: { xs: 1, sm: 0 },
                  }}
                >
                  Choose Your Path
                </Typography>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 2, sm: 3 }}
                  justifyContent="center"
                  sx={{ px: { xs: 1, sm: 0 } }}
                >
                  <Button
                    variant={userType === 'restaurant' ? 'contained' : 'outlined'}
                    size="large"
                    startIcon={<Iconify icon="mdi:food-fork-drink" />}
                    onClick={() => handleUserTypeChange('restaurant')}
                    sx={{
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 2, sm: 4 },
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      fontWeight: 'bold',
                      borderRadius: 2,
                      minWidth: { xs: '100%', sm: '180px', md: '200px' },
                      background:
                        userType === 'restaurant'
                          ? (theme) =>
                              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`
                          : 'transparent',
                      borderWidth: 3,
                      borderColor: (theme) => theme.palette.primary.main,
                      color:
                        userType === 'restaurant' ? 'white' : (theme) => theme.palette.primary.main,
                      '&:hover': {
                        borderWidth: 3,
                        transform: { xs: 'none', sm: 'translateY(-2px)' },
                        boxShadow: (theme) => theme.shadows[8],
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    I&apos;m a Restaurant
                  </Button>
                  <Button
                    variant={userType === 'supplier' ? 'contained' : 'outlined'}
                    size="large"
                    startIcon={<Iconify icon="mdi:truck-delivery" />}
                    onClick={() => handleUserTypeChange('supplier')}
                    sx={{
                      py: { xs: 1.5, sm: 2 },
                      px: { xs: 2, sm: 4 },
                      fontSize: { xs: '1rem', sm: '1.1rem' },
                      fontWeight: 'bold',
                      borderRadius: 2,
                      minWidth: { xs: '100%', sm: '180px', md: '200px' },
                      background:
                        userType === 'supplier'
                          ? (theme) =>
                              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`
                          : 'transparent',
                      borderWidth: 3,
                      borderColor: (theme) => theme.palette.primary.main,
                      color:
                        userType === 'supplier' ? 'white' : (theme) => theme.palette.primary.main,
                      '&:hover': {
                        borderWidth: 3,
                        transform: { xs: 'none', sm: 'translateY(-2px)' },
                        boxShadow: (theme) => theme.shadows[8],
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    I&apos;m a Supplier
                  </Button>
                </Stack>
              </Box>

              <Grid container spacing={{ xs: 2, sm: 3, md: 4, lg: 6 }} alignItems="center">
                {/* Left side - Content */}
                <Grid item xs={12} lg={6} order={{ xs: 2, lg: 1 }}>
                  <Stack spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ textAlign: 'left' }}>
                    {userTypeContent[userType].features.map((feature, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: { xs: 1.5, sm: 2 },
                          flexDirection: { xs: 'column', sm: 'row' },
                          textAlign: { xs: 'center', sm: 'left' },
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 40, sm: 48, md: 56, lg: 60 },
                            height: { xs: 40, sm: 48, md: 56, lg: 60 },
                            borderRadius: '50%',
                            background: (theme) =>
                              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            alignSelf: { xs: 'center', sm: 'flex-start' },
                          }}
                        >
                          <Iconify
                            icon={feature.icon}
                            sx={{
                              color: 'white',
                              width: { xs: 24, sm: 28, md: 30 },
                              height: { xs: 24, sm: 28, md: 30 },
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            sx={{
                              mb: 1,
                              fontWeight: 'bold',
                              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                            }}
                          >
                            {feature.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}
                          >
                            {feature.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Grid>

                {/* Right side - Screenshots */}
                <Grid item xs={12} lg={6} order={{ xs: 1, lg: 2 }}>
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mb: { xs: 2, lg: 0 },
                      mt: { xs: 1, lg: 0 },
                      width: '100%',
                      px: { xs: 0, sm: 0 },
                      mx: 'auto',
                    }}
                  >
                    {/* Screenshot Image - Cell Phone Size */}
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        maxWidth: '100%',
                        '& img': {
                          maxHeight: { xs: '300px', sm: '400px', md: '450px', lg: '500px' },
                          width: 'auto',
                          height: 'auto',
                          display: 'block',
                          maxWidth: '100%',
                        },
                      }}
                    >
                      <img
                        src={userTypeContent[userType].screenshots[currentScreenshot]}
                        alt={`${userType} screenshot ${currentScreenshot + 1}`}
                        onError={(e) => {
                          // Fallback to placeholder if image fails to load
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      {/* Fallback placeholder if image fails to load */}
                      <Box
                        sx={{
                          width: '100%',
                          minHeight: '200px',
                          background:
                            'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
                          backgroundSize: '20px 20px',
                          backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
                          display: 'none',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                          gap: 2,
                          p: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            textAlign: 'center',
                            fontWeight: 'bold',
                            color: 'text.primary',
                            mb: 1,
                          }}
                        >
                          Screenshot {currentScreenshot + 1}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textAlign: 'center', px: 2 }}
                        >
                          {userType === 'restaurant' ? 'Restaurant' : 'Supplier'} Platform
                        </Typography>
                      </Box>
                    </Box>

                    {/* Left Navigation Button */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: { xs: 8, sm: 16, md: 24 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 3,
                        display: 'block',
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={handlePrevScreenshot}
                        sx={{
                          minWidth: { xs: 36, sm: 40, md: 44 },
                          width: { xs: 36, sm: 40, md: 44 },
                          height: { xs: 36, sm: 40, md: 44 },
                          borderRadius: '50%',
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: 'text.primary',
                          boxShadow: (theme) => theme.shadows[4],
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 1)',
                            boxShadow: (theme) => theme.shadows[8],
                          },
                        }}
                      >
                        <Iconify
                          icon="mdi:chevron-left"
                          width={{ xs: 20, sm: 24, md: 28 }}
                          height={{ xs: 20, sm: 24, md: 28 }}
                        />
                      </Button>
                    </Box>

                    {/* Right Navigation Button */}
                    <Box
                      sx={{
                        position: 'absolute',
                        right: { xs: 8, sm: 16, md: 24 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 3,
                        display: 'block',
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleNextScreenshot}
                        sx={{
                          minWidth: { xs: 36, sm: 40, md: 44 },
                          width: { xs: 36, sm: 40, md: 44 },
                          height: { xs: 36, sm: 40, md: 44 },
                          borderRadius: '50%',
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: 'text.primary',
                          boxShadow: (theme) => theme.shadows[4],
                          '&:hover': {
                            background: 'rgba(255, 255, 255, 1)',
                            boxShadow: (theme) => theme.shadows[8],
                          },
                        }}
                      >
                        <Iconify
                          icon="mdi:chevron-right"
                          width={{ xs: 20, sm: 24, md: 28 }}
                          height={{ xs: 20, sm: 24, md: 28 }}
                        />
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </m.div>
      </Container>
    </StyledRoot>
  );
}
